import { GetServerSideProps } from "next"
import { findResultsState } from "react-instantsearch-dom/server"

import { AppProviders } from "./_app"
import SearchPageContainer from "containers/Search/SearchPageContainer"

import searchClient from "utils/algolia/searchClient"
import { urlToSearchState } from "@secureo/common/hooks/useAlgoliaURLSync"
import normalizeLocale from "@secureo/common/utils/normalizeLocale"
import serializeJSON from "@secureo/common/utils/serializeJSON"
import { supportedValidLocales } from "utils/i18n/supportedLocalesAndCurrencyCodes"
import { getServerTranslations } from "utils/i18n/getServerTranslations"

const SearchPage = (props: Props) => {
	return <SearchPageContainer {...props} />
}

export const getServerSideProps: GetServerSideProps = async (context) => {
	const { req, res, locale: rawLocale } = context
	const { locale } = normalizeLocale(rawLocale)

	if (!supportedValidLocales.includes(locale)) {
		res.statusCode = 404
		return {
			notFound: true,
			props: {
				statusCode: 404,
				error: "Locale not supported",
			},
		}
	}

	// Fetch initial resultState from Algolia
	const indexName = `${locale}-products`
	const searchState = urlToSearchState(req.url)
	let resultsState = null

	const localizationProps = await getServerTranslations(locale, [
		"common",
		"countries",
		"languages",
		"category",
	])

	try {
		resultsState = await findResultsState(
			(props) => (
				<AppProviders>
					<SearchPageContainer
						initialSearchState={searchState}
						locale={locale}
						{...localizationProps}
						{...props}
					/>
				</AppProviders>
			),
			{
				indexName,
				searchClient,
				searchState,
			},
		)
	} catch (err) {
		console.error(err)
		res.statusCode = 500
		return {
			props: {
				statusCode: 500,
				error: "Failed to create resultsState",
			},
		}
	}

	const props: Props = {
		indexName,
		resultsState,
		initialSearchState: searchState,
		asPath: req.url,
		...localizationProps,
	}

	const oneHoursInSeconds = 60 * 60

	res.setHeader(
		"Cache-Control",
		`s-maxage=30, stale-while-revalidate=${oneHoursInSeconds * 12 - 30}`,
	)

	return {
		props: serializeJSON(props),
	}
}

interface Props {
	indexName: string
	resultsState: any
	initialSearchState: any
	onSearchParameters?: () => any
	asPath: string
}

export default SearchPage
